import React, { useEffect, useRef } from 'react';
import { Timeline, Tag, Typography } from 'antd';
import {
  BulbOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;

/**
 * 思考日志组件 — 通过 Ant Design Timeline 展示 Agent 推理过程
 *
 * 视觉规则:
 * - thinking   → 蓝色 (#1677ff)  + BulbOutlined
 * - arbitration → 橙色 (#fa8c16) + AuditOutlined
 * - done       → 绿色 (#52c41a) + CheckCircleOutlined
 *
 * 每条记录展示:
 *   - Agent 名称 (Tag)
 *   - 时间戳
 *   - reasoning 内容 (紫色高亮背景)
 *   - refData (紫色 Tag)
 *
 * @param {Array}  logs  — SSE streamData 数组, 每项 { type, time, data }
 * @param {boolean} autoScroll — 是否自动滚动到底部
 * @param {number} maxHeight — 最大高度 (px), 默认 400
 */
export default function ThinkingLog({ logs = [], autoScroll = true, maxHeight = 400 }) {
  const containerRef = useRef(null);

  // 滚轮自动滚动到底部
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  if (!logs || logs.length === 0) {
    return (
      <div style={styles.empty}>
        <ClockCircleOutlined style={{ fontSize: 24, color: 'var(--text-muted)', opacity: 0.4 }} />
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          等待 Agent 思考日志推送...
        </div>
      </div>
    );
  }

  const items = logs.map((entry, idx) => {
    const config = TYPE_CONFIG[entry.type] || TYPE_CONFIG.thinking;
    const ts = formatTime(entry.time);
    const d = entry.data || {};

    return {
      key: idx,
      color: config.color,
      dot: (
        <div style={{
          ...styles.dotBase,
          background: config.color,
          boxShadow: `0 0 8px ${config.color}55`,
        }}>
          {config.icon}
        </div>
      ),
      children: (
        <div style={styles.logEntry}>
          {/* 行 Top: Agent名称 + 类型Tag + 时间 */}
          <div style={styles.logMeta}>
            {d.agent && (
              <Tag color={config.tagColor} style={styles.agentTag}>
                {d.agent}
              </Tag>
            )}
            <Tag bordered={false} color={config.tagColor} style={{ fontSize: 10, lineHeight: '18px', padding: '0 4px' }}>
              {config.label}
            </Tag>
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
              {ts}
            </Text>
          </div>

          {/* reasoning: 紫色高亮背景 */}
          {d.reasoning && (
            <Paragraph
              style={styles.reasoning}
              copyable={false}
            >
              {d.reasoning}
            </Paragraph>
          )}

          {/* 附加摘要 */}
          {d.summary && (
            <div style={styles.summary}>{d.summary}</div>
          )}

          {/* refData: 紫色 Tag 列表 */}
          {d.refData && (
            <div style={styles.refDataRow}>
              {(Array.isArray(d.refData) ? d.refData : [d.refData]).map((ref, ri) => (
                <Tag
                  key={ri}
                  color="purple"
                  style={{ fontSize: 10, marginBottom: 2 }}
                >
                  {typeof ref === 'object' ? JSON.stringify(ref) : String(ref)}
                </Tag>
              ))}
            </div>
          )}

          {/* 可选: 结构化数据展示 */}
          {d.score !== undefined && (
            <div style={styles.scoreLine}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Score:</span>
              <span style={{ color: config.color, fontWeight: 600, fontSize: 13, marginLeft: 6 }}>
                {d.score}
              </span>
              {d.confidence !== undefined && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 10 }}>
                  conf: {Math.round(d.confidence * 100)}%
                </span>
              )}
            </div>
          )}

          {/* 任意额外文本 */}
          {d.text && !d.reasoning && !d.summary && (
            <Text style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.text}</Text>
          )}
        </div>
      ),
    };
  });

  return (
    <div ref={containerRef} style={{ ...styles.container, maxHeight }}>
      <Timeline mode="left" items={items} />
    </div>
  );
}

// ─── 类型配置 ────────────────────────────────────────────────────────────
const TYPE_CONFIG = {
  thinking: {
    color: '#1677ff',
    tagColor: 'blue',
    label: 'Thinking',
    icon: <BulbOutlined style={{ fontSize: 12, color: '#fff' }} />,
  },
  arbitration: {
    color: '#fa8c16',
    tagColor: 'orange',
    label: 'Arbitration',
    icon: <AuditOutlined style={{ fontSize: 12, color: '#fff' }} />,
  },
  done: {
    color: '#52c41a',
    tagColor: 'green',
    label: 'Done',
    icon: <CheckCircleOutlined style={{ fontSize: 12, color: '#fff' }} />,
  },
  message: {
    color: '#722ed1',
    tagColor: 'purple',
    label: 'Info',
    icon: <BulbOutlined style={{ fontSize: 12, color: '#fff' }} />,
  },
};

// ─── 工具函数 ──────────────────────────────────────────────────────────────
function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ─── 样式 ──────────────────────────────────────────────────────────────────
const styles = {
  container: {
    overflowY: 'auto',
    padding: '12px 16px 12px 8px',
    background: 'var(--surface-1, #1a1a2e)',
    borderRadius: 'var(--radius-md, 12px)',
    border: '1px solid var(--border-default, rgba(255,255,255,0.08))',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,255,255,0.1) transparent',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    background: 'var(--surface-1, #1a1a2e)',
    borderRadius: 'var(--radius-md, 12px)',
    border: '1px solid var(--border-default, rgba(255,255,255,0.08))',
  },
  dotBase: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  logEntry: {
    paddingBottom: 4,
  },
  logMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
    flexWrap: 'wrap',
  },
  agentTag: {
    fontWeight: 600,
    fontSize: 11,
    lineHeight: '18px',
    padding: '0 6px',
  },
  reasoning: {
    margin: '6px 0 8px',
    padding: '10px 14px',
    background: 'linear-gradient(135deg, rgba(114,46,209,0.08) 0%, rgba(114,46,209,0.15) 100%)',
    border: '1px solid rgba(114,46,209,0.2)',
    borderRadius: 'var(--radius-sm, 8px)',
    borderLeft: '3px solid #722ed1',
    color: 'var(--text-primary, #e8e8e8)',
    fontSize: 13,
    lineHeight: 1.6,
  },
  summary: {
    fontSize: 12,
    color: 'var(--text-secondary, #b0b0c0)',
    marginBottom: 6,
    lineHeight: 1.5,
  },
  refDataRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  },
  scoreLine: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 6,
  },
};

export { TYPE_CONFIG as THINKING_LOG_TYPES };
